/**
 Copyright Highway9 Networks Inc.
 */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "..";
import { DailyReport, DailyReportQuery } from "~/types/DailyReport";
import dailyReportService from "~/services/daily-report-service";

export const dailyReportsAdapter = createEntityAdapter<DailyReport>();

interface DailyReportState {
  reportsData: EntityState<DailyReport>;
  selectedId: string | null;
  loading: boolean;
}

export const initialState: DailyReportState = {
  reportsData: dailyReportsAdapter.getInitialState(),
  selectedId: null,
  loading: true,
};

export const fetchDailyReports = createAsyncThunk(
  "dailyReport/fetchDailyReports",
  async (query: DailyReportQuery) => {
    const response = await dailyReportService.getDailyReports(query);
    return response;
  }
);

const dailyReportSlice = createSlice({
  name: "dailyReport",
  initialState,
  reducers: {
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDailyReports.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchDailyReports.fulfilled, (state, action) => {
      state.loading = false;
      // Keep the existing entity state when nothing material changed, so the
      // generation poll (refetch every 10s) doesn't re-render the whole page —
      // replacing data with identical content re-parses every markdown finding
      // and shows as screen flicker (MCS-4151).
      const fingerprint = (rows: DailyReport[]) =>
        rows.map((r) => `${r.id}:${r.generatedAt ?? 0}:${r.updationTime ?? 0}`).join("|");
      const existing = dailyReportsAdapter.getSelectors().selectAll(state.reportsData);
      if (fingerprint(existing) !== fingerprint(action.payload)) {
        // Payload is newest-first; setAll preserves that order in ids.
        dailyReportsAdapter.setAll(state.reportsData, action.payload);
      }
      // Default selection: newest report (data is returned newest-first).
      if (!state.selectedId && action.payload.length > 0) {
        state.selectedId = action.payload[0].id;
      }
    });
    builder.addCase(fetchDailyReports.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const dailyReportActions = dailyReportSlice.actions;

const dailyReportEntitySelectors = dailyReportsAdapter.getSelectors(
  (state: RootState) => state.dailyReport.reportsData,
);

export const dailyReportData = dailyReportEntitySelectors.selectAll;
export const dailyReportEntities = dailyReportEntitySelectors.selectEntities;

export const dailyReportState = createSelector(
  [(state: RootState) => state.dailyReport.selectedId, dailyReportEntities],
  (selectedId, entities): DailyReport | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const dailyReportLoading = (state: RootState) => state.dailyReport.loading;

export default dailyReportSlice.reducer;
